import { useEffect, useRef, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { api, eur, STATUS } from "@/lib/api";
import { submitRedsys } from "@/pages/Checkout";

export default function PaymentResult() {
  const [params] = useSearchParams();
  const { user } = useAuth();
  const { clear } = useCart();
  const [order, setOrder] = useState(null);
  const [tries, setTries] = useState(0);
  const cleared = useRef(false);
  const orderId = params.get("order");
  const ko = params.get("status") === "ko";

  useEffect(() => {
    if (!user || !orderId) return;
    const mp = params.get("Ds_MerchantParameters");
    const sig = params.get("Ds_Signature");
    const load = () => api.get(`/orders/${orderId}`).then((r) => setOrder(r.data)).catch(() => {});
    if (mp && sig) {
      api.post("/payments/redsys/return", { Ds_SignatureVersion: params.get("Ds_SignatureVersion"), Ds_MerchantParameters: mp, Ds_Signature: sig })
        .then((r) => setOrder(r.data)).catch(load);
    } else load();
  }, [user, orderId, params]);

  useEffect(() => {
    if (!cleared.current && ["pagado", "enviado", "entregado"].includes(order?.status)) {
      cleared.current = true;
      clear();
      localStorage.removeItem("faunny_pending_order");
    }
    if (order?.status === "pendiente_pago" && !ko && tries < 6) {
      const t = setTimeout(() => { api.get(`/orders/${orderId}`).then((r) => setOrder(r.data)); setTries(tries + 1); }, 2500);
      return () => clearTimeout(t);
    }
  }, [order, ko, tries, orderId, clear]);

  const paid = order && ["pagado", "enviado", "entregado"].includes(order.status);
  const retry = async () => { const { data } = await api.post(`/orders/${orderId}/retry`); submitRedsys(data.redsys); };

  if (user === false) return <Navigate to={`/login?next=${encodeURIComponent(`/pedido/resultado${window.location.search}`)}`} replace />;
  if (!order) return <div className="py-32 grid place-items-center"><Loader2 className="animate-spin text-[#2563EB]" size={40} /></div>;

  return (
    <div className="max-w-2xl mx-auto px-4 py-20 text-center" data-testid="payment-result-page">
      {paid ? <CheckCircle2 size={64} className="mx-auto text-[#2563EB]" /> : ko || order.status === "fallido" ? <XCircle size={64} className="mx-auto text-[#DC2626]" /> : <Loader2 size={64} className="mx-auto text-[#2563EB] animate-spin" />}
      <h1 className="font-display font-black text-5xl text-[#0B1D3A] mt-6" data-testid="payment-result-title">
        {paid ? "Pedido confirmado" : ko || order.status === "fallido" ? "Pago no completado" : "Confirmando el pago…"}
      </h1>
      <p className="text-slate-600 mt-4" data-testid="payment-result-text">
        {paid ? <>Gracias por tu compra. Te hemos enviado un email con el resumen del pedido <strong>{order.number}</strong>.</>
          : ko || order.status === "fallido" ? "El pago se ha cancelado o ha sido rechazado. Puedes volver a intentarlo."
          : `Estamos esperando la confirmación de Redsys. Estado actual: ${STATUS[order.status]}.`}
      </p>
      <div className="mt-8 bg-[#EFF6FF] rounded p-5 text-left text-sm space-y-1">
        {order.items.map((i) => <div key={i.slug + i.format_id} className="flex justify-between"><span>{i.name} · {i.format_label} × {i.qty}</span><span>{eur(i.price * i.qty)}</span></div>)}
        <div className="flex justify-between font-bold pt-2 border-t border-blue-200 mt-2"><span>Total</span><span className="text-[#DC2626]">{eur(order.total)}</span></div>
      </div>
      <div className="mt-8 flex flex-wrap justify-center gap-3">
        {!paid && <button onClick={retry} data-testid="payment-retry-button" className="bg-[#DC2626] hover:bg-[#B91C1C] text-white font-display font-bold px-6 py-3 rounded-full">Reintentar pago</button>}
        <Link to="/cuenta" data-testid="payment-account-link" className="bg-[#0B1D3A] text-white font-display font-bold px-6 py-3 rounded-full">Ver mis pedidos</Link>
        <Link to="/tienda" className="border-2 border-[#0B1D3A] text-[#0B1D3A] font-display font-bold px-6 py-3 rounded-full">Seguir comprando</Link>
      </div>
    </div>
  );
}
